import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import moment from 'moment';

import Button, {
  ButtonSize,
  ButtonType
} from '../shared/components/core-ui/button/button.component';
import { birdToast } from '../shared/components/core-ui/toast';
import { Pagination } from '../shared/components/core-ui/pagination/pagination.component';
import { isErrorInfo } from '../shared/interfaces/error-info.interface';
import {
  GuestAudienceFilterOptionsModel,
  GuestAudienceQueryModel,
  GuestAudienceRowModel,
  GuestAudienceSearchModel,
  GuestAudienceSortBy
} from '../shared/models/guest-audience.model';
import { LoadingData } from '../ui/LoadingData';
import { NotFoundData } from '../ui/NotFoundData';
import {
  exportGuestAudience,
  getGuestAudience,
  getGuestAudienceFilterOptions
} from './guest-audience.service';

const selectMenuPortalStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 })
};

const EMPTY_OPTIONS: GuestAudienceFilterOptionsModel = {
  statuses: [],
  cities: [],
  areas: [],
  properties: []
};

const SORT_OPTIONS: { value: GuestAudienceSortBy; label: string }[] = [
  { value: 'bookings', label: 'Most bookings' },
  { value: 'lastCheckIn', label: 'Most recent stay' },
  { value: 'name', label: 'Name (A–Z)' }
];

/**
 * Statuses selected when the page first loads.
 *
 * Left empty, which the report reads as "every status except canceled and expired" — a guest who
 * only ever abandoned a booking is not an audience. Marketing can add those statuses explicitly.
 */
const DEFAULT_STATUSES: string[] = [];

type StringOption = { value: string; label: string };

const formatMoney = (value?: number) => {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

const formatDate = (ms?: number) => {
  if (!ms) return '—';
  return moment(ms).format('DD MMM YYYY');
};

/** Reads a react-select multi value as a plain array of ids. */
const toIds = (value: ValueType<StringOption, true>): string[] =>
  ((value as StringOption[]) || []).map((option) => option.value);

const COLUMNS = [
  'Name',
  'Email',
  'Phone',
  'Nationality',
  'Bookings',
  'Paid (EGP)',
  'Paid (USD)',
  'Paid (EGP equiv.)',
  'First stay',
  'Last stay',
  'Last booked',
  'Cities',
  'Projects'
];

const GuestAudienceReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const [rows, setRows] = useState<GuestAudienceRowModel[]>([]);
  const [count, setCount] = useState(0);
  const [truncated, setTruncated] = useState(false);

  const [offset, setOffset] = useState(1);
  const [limit, setLimit] = useState(20);

  const [sortBy, setSortBy] = useState<GuestAudienceSortBy>('bookings');

  const [statuses, setStatuses] = useState<string[]>(DEFAULT_STATUSES);
  const [cityIds, setCityIds] = useState<string[]>([]);
  const [areaIds, setAreaIds] = useState<string[]>([]);
  const [propertyIds, setPropertyIds] = useState<string[]>([]);

  const [createdFrom, setCreatedFrom] = useState<Date | null>(null);
  const [createdTo, setCreatedTo] = useState<Date | null>(null);
  const [checkInFrom, setCheckInFrom] = useState<Date | null>(null);
  const [checkInTo, setCheckInTo] = useState<Date | null>(null);
  const [checkOutFrom, setCheckOutFrom] = useState<Date | null>(null);
  const [checkOutTo, setCheckOutTo] = useState<Date | null>(null);

  const [options, setOptions] = useState<GuestAudienceFilterOptionsModel>(EMPTY_OPTIONS);

  useEffect(() => {
    getGuestAudienceFilterOptions().then((response) => {
      if (!isErrorInfo(response)) {
        setOptions(response);
      } else {
        birdToast.error('Filters could not be loaded', 'Guest audience');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // City narrows areas, area narrows projects. Selecting a city and then an area inside it narrows
  // to that area — the server intersects the two rather than widening back to the city.
  const cityOptions = useMemo(
    () => options.cities.map((city) => ({ value: city.id, label: city.name })),
    [options.cities]
  );

  const areasInScope = useMemo(
    () => options.areas.filter((area) => cityIds.length === 0 || cityIds.includes(area.cityId)),
    [cityIds, options.areas]
  );

  const areaOptions = useMemo(
    () => areasInScope.map((area) => ({ value: area.id, label: area.name })),
    [areasInScope]
  );

  const propertyOptions = useMemo(() => {
    // With no city or area chosen, every project is offered — filtering by the full area list
    // instead would quietly hide any project whose areaId is blank.
    if (cityIds.length === 0 && areaIds.length === 0) {
      return options.properties.map((property) => ({
        value: property.id,
        label: property.name
      }));
    }

    const scopedAreaIds = areaIds.length > 0 ? areaIds : areasInScope.map((area) => area.id);

    return options.properties
      .filter((property) => scopedAreaIds.includes(property.areaId))
      .map((property) => ({ value: property.id, label: property.name }));
  }, [areaIds, areasInScope, cityIds, options.properties]);

  const statusOptions = useMemo(
    () => options.statuses.map((status) => ({ value: status.id, label: status.name })),
    [options.statuses]
  );

  /**
   * The filters as the API wants them.
   *
   * Both ends of every range are inclusive, so a "to" date is sent as the last instant of that
   * day. Sending its midnight instead would drop everything that happened during the day the
   * operator picked, which is the kind of off-by-one nobody notices until a mailing list is short.
   */
  const buildQuery = useCallback((): GuestAudienceQueryModel => {
    return {
      ...(statuses.length > 0 ? { status: statuses } : {}),
      ...(cityIds.length > 0 ? { cityIds } : {}),
      ...(areaIds.length > 0 ? { areaIds } : {}),
      ...(propertyIds.length > 0 ? { propertyIds } : {}),
      ...(createdFrom ? { createdFrom: moment(createdFrom).startOf('day').valueOf() } : {}),
      ...(createdTo ? { createdTo: moment(createdTo).endOf('day').valueOf() } : {}),
      ...(checkInFrom ? { checkInFrom: moment(checkInFrom).startOf('day').valueOf() } : {}),
      ...(checkInTo ? { checkInTo: moment(checkInTo).endOf('day').valueOf() } : {}),
      ...(checkOutFrom ? { checkOutFrom: moment(checkOutFrom).startOf('day').valueOf() } : {}),
      ...(checkOutTo ? { checkOutTo: moment(checkOutTo).endOf('day').valueOf() } : {})
    };
  }, [
    areaIds,
    checkInFrom,
    checkInTo,
    checkOutFrom,
    checkOutTo,
    cityIds,
    createdFrom,
    createdTo,
    propertyIds,
    statuses
  ]);

  const loadAudience = useCallback(
    (page: number, perPage: number, sort: GuestAudienceSortBy) => {
      setLoading(true);
      setError(false);

      const search: GuestAudienceSearchModel = {
        offset: page,
        limit: perPage,
        sortBy: sort,
        sortDirection: sort === 'name' ? 'asc' : 'desc',
        query: buildQuery()
      };

      getGuestAudience(search).then((response) => {
        setLoading(false);
        setHasSearched(true);

        if (isErrorInfo(response)) {
          setError(true);
          setRows([]);
          setCount(0);
          setTruncated(false);
          return;
        }

        setRows(response.rows);
        setCount(response.count);
        setTruncated(response.truncated);
      });
    },
    [buildQuery]
  );

  /** Date ranges only make sense one way round; a reversed pair returns nothing, silently. */
  const validateRanges = (): boolean => {
    const pairs: [Date | null, Date | null, string][] = [
      [createdFrom, createdTo, 'Booked'],
      [checkInFrom, checkInTo, 'Check-in'],
      [checkOutFrom, checkOutTo, 'Check-out']
    ];

    const reversed = pairs.find(
      ([from, to]) => from && to && moment(to).isBefore(moment(from), 'day')
    );

    if (reversed) {
      birdToast.error(`${reversed[2]} "to" date is before its "from" date`, 'Guest audience');
      return false;
    }

    return true;
  };

  const handleGenerate = () => {
    if (!validateRanges()) {
      return;
    }
    setOffset(1);
    loadAudience(1, limit, sortBy);
  };

  const handleSortChange = (next: GuestAudienceSortBy) => {
    if (next === sortBy) {
      return;
    }
    setSortBy(next);
    setOffset(1);
    if (hasSearched) {
      loadAudience(1, limit, next);
    }
  };

  const handleClearFilters = () => {
    setStatuses(DEFAULT_STATUSES);
    setCityIds([]);
    setAreaIds([]);
    setPropertyIds([]);
    setCreatedFrom(null);
    setCreatedTo(null);
    setCheckInFrom(null);
    setCheckInTo(null);
    setCheckOutFrom(null);
    setCheckOutTo(null);
  };

  const downloadCSV = (file: Blob | string) => {
    const link = document.createElement('a');
    const csvBlob = file instanceof Blob ? file : new Blob([file], { type: 'text/csv' });
    link.href = window.URL.createObjectURL(csvBlob);
    link.setAttribute('download', `guest-audience-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(link.href);
    document.body.removeChild(link);
  };

  /**
   * Exports every guest the current filters produce, not the visible page — the page is a preview
   * of the list, and the list is what marketing needs.
   */
  const handleExport = () => {
    if (!validateRanges()) {
      return;
    }

    setExporting(true);
    exportGuestAudience({
      sortBy,
      sortDirection: sortBy === 'name' ? 'asc' : 'desc',
      query: buildQuery()
    }).then((response) => {
      setExporting(false);

      if (isErrorInfo(response)) {
        birdToast.error('Export failed', 'Guest audience');
        return;
      }

      downloadCSV(response);
    });
  };

  const handlePageChange = (newPage: number) => {
    setOffset(newPage);
    loadAudience(newPage, limit, sortBy);
  };

  const handleItemsPerPage = (itemsPerPage: number) => {
    setLimit(itemsPerPage);
    setOffset(1);
    loadAudience(1, itemsPerPage, sortBy);
  };

  return (
    <div className="min-h-screen bg-creams-50 px-6 py-4">
      <section className="sticky top-0 z-30 -mx-6 mb-6 flex flex-col gap-4 border-b border-gray-200 bg-creams-50 px-6 py-4">
        <div>
          <h1 className="text-4xl font-recoleta font-semibold text-neutral-500">Guest Audience</h1>
          <p className="mt-1 text-base font-gilroy-medium text-neutral-400">
            One row per guest, with how many times they have booked and what they have paid us.
            Bookings are matched to a guest by email, or by phone where no email was captured, so a
            repeat guest appears once. Every date range is{' '}
            <span className="font-gilroy-bold">inclusive</span> of both ends.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Reservation Status
            </label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={statusOptions}
              placeholder="All but canceled & expired"
              value={statusOptions.filter((option) => statuses.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => setStatuses(toIds(value))}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">City</label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={cityOptions}
              placeholder="All cities"
              value={cityOptions.filter((option) => cityIds.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => {
                setCityIds(toIds(value));
                setAreaIds([]);
                setPropertyIds([]);
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">Area</label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={areaOptions}
              placeholder="All areas"
              value={areaOptions.filter((option) => areaIds.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => {
                setAreaIds(toIds(value));
                setPropertyIds([]);
              }}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Project
            </label>
            <Select
              isMulti
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={propertyOptions}
              placeholder="All projects"
              value={propertyOptions.filter((option) => propertyIds.includes(option.value))}
              classNamePrefix="react-select"
              onChange={(value: ValueType<StringOption, true>) => setPropertyIds(toIds(value))}
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Booked From
            </label>
            <ReactDatePicker
              selected={createdFrom}
              onChange={(date: Date | null) => setCreatedFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Booked To
            </label>
            <ReactDatePicker
              selected={createdTo}
              minDate={createdFrom ?? undefined}
              onChange={(date: Date | null) => setCreatedTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-in From
            </label>
            <ReactDatePicker
              selected={checkInFrom}
              onChange={(date: Date | null) => setCheckInFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-in To
            </label>
            <ReactDatePicker
              selected={checkInTo}
              minDate={checkInFrom ?? undefined}
              onChange={(date: Date | null) => setCheckInTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-out From
            </label>
            <ReactDatePicker
              selected={checkOutFrom}
              onChange={(date: Date | null) => setCheckOutFrom(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Check-out To
            </label>
            <ReactDatePicker
              selected={checkOutTo}
              minDate={checkOutFrom ?? undefined}
              onChange={(date: Date | null) => setCheckOutTo(date)}
              placeholderText="Any"
              isClearable
              className="w-full h-[38px] px-3 rounded border border-gray-300 text-sm"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-gilroy-medium text-neutral-400">
              Sort By
            </label>
            <Select
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={selectMenuPortalStyles}
              options={SORT_OPTIONS}
              value={SORT_OPTIONS.find((option) => option.value === sortBy) ?? null}
              classNamePrefix="react-select"
              onChange={(value: ValueType<{ value: GuestAudienceSortBy; label: string }, false>) =>
                handleSortChange(value?.value || 'bookings')
              }
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            onClick={handleGenerate}
            type={ButtonType.primary}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Generate
          </Button>
          <Button
            onClick={handleClearFilters}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            Clear filters
          </Button>
          <Button
            onClick={handleExport}
            type={ButtonType.primaryInverted}
            size={ButtonSize.small}
            disabled={!hasSearched || exporting}
            classNameButton="px-5 py-3 text-sm leading-4"
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
          {hasSearched && !loading && !error && (
            <span className="text-sm font-gilroy-medium text-neutral-400">
              {count} guest{count === 1 ? '' : 's'}
            </span>
          )}
        </div>
      </section>

      <section>
        {loading && <LoadingData />}

        {!loading && error && (
          <div className="flex flex-col items-center py-10">
            <p className="text-base font-gilroy-medium text-neutral-400">
              The report could not be loaded.
            </p>
            <Button
              onClick={() => loadAudience(offset, limit, sortBy)}
              type={ButtonType.primary}
              size={ButtonSize.small}
              className="mt-6"
              classNameButton="px-5 py-3 text-sm leading-4"
            >
              Retry
            </Button>
          </div>
        )}

        {!loading && !error && !hasSearched && (
          <p className="py-10 text-center text-base font-gilroy-medium text-neutral-400">
            Choose your filters and hit Generate. With none set, the report returns every guest who
            has ever stayed with us.
          </p>
        )}

        {!loading && !error && hasSearched && (
          <>
            {truncated && (
              <p className="mb-4 rounded-xl border border-warnings-200 bg-warnings-50 px-4 py-3 text-sm font-gilroy-medium text-warnings-700">
                This selection has more guests than one report can carry, so the figures below and
                any export are incomplete. Narrow the filters — by city, project or date — and run
                it again.
              </p>
            )}

            {rows.length === 0 ? (
              <NotFoundData />
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-md">
                <table className="w-full min-w-[1400px] text-left text-sm">
                  <thead>
                    <tr className="bg-creams-50">
                      {COLUMNS.map((column) => (
                        <th
                          key={column}
                          className="whitespace-nowrap px-3 py-3 font-gilroy-bold text-neutral-500"
                        >
                          {column}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.guestKey} className="hover:bg-primary-50/40">
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.name || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.email || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.phone || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {row.nationality || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right">
                          {row.bookingsCount}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap">
                          {formatMoney(row.totalPaidEgp)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap">
                          {formatMoney(row.totalPaidUsd)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 text-right whitespace-nowrap font-gilroy-bold">
                          {formatMoney(row.totalPaidEgpEquivalent)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.firstCheckInDate)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.lastCheckInDate)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3 whitespace-nowrap">
                          {formatDate(row.lastBookedAt)}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3">
                          {(row.cityNames || []).join(', ') || '—'}
                        </td>
                        <td className="border-t border-gray-100 px-3 py-3">
                          {(row.propertyNames || []).join(', ') || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {count > 0 && (
              <div className="flex items-center justify-center pb-4">
                <Pagination
                  itemCounts={count}
                  itemsPerPage={limit}
                  offset={offset}
                  handlePageChange={handlePageChange}
                  handleItemsPerPage={handleItemsPerPage}
                />
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default GuestAudienceReportPage;
